import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Star, Truck, Shield, ExternalLink } from 'lucide-react';
import axios from 'axios';
import ShopLayout from '../components/layout/ShopLayout';
import ProductCard from '../components/ProductCard';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const ProductPage = () => {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      try {
        const [productRes, productsRes] = await Promise.all([
          axios.get(`${API}/api/products/${id}`),
          axios.get(`${API}/api/products`)
        ]);
        
        setProduct(productRes.data);
        
        // Get related products from same category
        const related = productsRes.data
          .filter(p => p.category === productRes.data.category && p.id !== id)
          .slice(0, 4);
        setRelatedProducts(related);
      } catch (error) {
        console.error('Error fetching product:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [id]);

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating);
    const hasHalf = rating % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      stars.push(
        <Star
          key={i}
          className={`w-5 h-5 ${
            i < fullStars
              ? 'fill-yellow-400 text-yellow-400'
              : i === fullStars && hasHalf
              ? 'fill-yellow-400/50 text-yellow-400'
              : 'text-neutral-300'
          }`}
        />
      );
    }
    return stars;
  };

  if (loading) {
    return (
      <ShopLayout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="spinner"></div>
        </div>
      </ShopLayout>
    );
  }

  if (!product) {
    return (
      <ShopLayout>
        <div className="min-h-[60vh] flex flex-col items-center justify-center">
          <p className="text-neutral-500 mb-4">Product not found</p>
          <Link to="/">
            <Button variant="outline" className="rounded-none">Back to Home</Button>
          </Link>
        </div>
      </ShopLayout>
    );
  }

  return (
    <ShopLayout>
      {/* Breadcrumb */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12 py-4">
        <div className="flex items-center gap-2 text-sm text-neutral-500">
          <Link to="/" className="hover:text-neutral-900 transition-colors">Home</Link>
          <ChevronRight className="w-4 h-4" />
          <Link to={`/category/${product.category}`} className="hover:text-neutral-900 transition-colors capitalize">
            {product.category.replace('-', ' & ')}
          </Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-neutral-900 truncate">{product.name}</span>
        </div>
      </div>

      {/* Product Details */}
      <section className="py-8 md:py-12" data-testid="product-detail-section">
        <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            {/* Image Gallery */}
            <div className="space-y-4">
              <div className="aspect-square bg-secondary overflow-hidden">
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-main-image"
                />
              </div>
            </div>

            {/* Product Info */}
            <div className="lg:py-8">
              {/* Brand */}
              <p className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">
                {product.brand}
              </p>

              {/* Name */}
              <h1 className="font-heading text-3xl md:text-4xl font-medium mb-4" data-testid="product-name">
                {product.name}
              </h1>

              {/* Rating */}
              <div className="flex items-center gap-3 mb-6">
                <div className="flex items-center gap-1">
                  {renderStars(product.rating)}
                </div>
                <span className="text-sm text-neutral-500">
                  {product.rating.toFixed(1)} ({product.review_count.toLocaleString()} reviews on Amazon)
                </span>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-3 mb-6">
                <span className="text-3xl font-semibold" data-testid="product-price">
                  ${product.price.toFixed(2)}
                </span>
                {product.original_price && product.original_price > product.price && (
                  <>
                    <span className="text-xl text-neutral-400 line-through">
                      ${product.original_price.toFixed(2)}
                    </span>
                    <span className="px-2 py-1 bg-red-100 text-red-700 text-sm font-medium">
                      Save ${(product.original_price - product.price).toFixed(2)}
                    </span>
                  </>
                )}
              </div>

              {/* Badges */}
              <div className="flex flex-wrap gap-2 mb-6">
                {product.is_bestseller && (
                  <span className="badge-bestseller px-3 py-1 text-xs uppercase tracking-widest font-bold">
                    Bestseller
                  </span>
                )}
                {product.is_new && (
                  <span className="badge-new px-3 py-1 text-xs uppercase tracking-widest font-bold">
                    New Arrival
                  </span>
                )}
              </div>

              {/* Description */}
              <div className="mb-8">
                <h3 className="text-sm uppercase tracking-widest font-bold mb-3">Description</h3>
                <p className="text-neutral-600 leading-relaxed" data-testid="product-description">
                  {product.description}
                </p>
              </div>

              {/* Buy on Amazon Button */}
              <a
                href={product.amazon_url}
                target="_blank"
                rel="noopener noreferrer"
                className="amazon-btn block w-full text-center py-4 px-8 uppercase tracking-widest text-sm font-bold mb-6"
                data-testid="buy-on-amazon-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  Buy on Amazon
                  <ExternalLink className="w-4 h-4" />
                </span>
              </a>

              {/* Trust Badges */}
              <div className="grid grid-cols-2 gap-4 pt-6 border-t">
                <div className="flex items-center gap-3">
                  <Truck className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium">Fast Shipping</p>
                    <p className="text-xs text-neutral-500">Via Amazon Prime</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5 text-neutral-400" />
                  <div>
                    <p className="text-sm font-medium">Authentic Products</p>
                    <p className="text-xs text-neutral-500">100% Genuine</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <section className="py-16 md:py-24 bg-secondary" data-testid="related-products-section">
          <div className="max-w-[1600px] mx-auto px-4 md:px-8 lg:px-12">
            <h2 className="font-heading text-2xl md:text-3xl font-normal mb-8 text-center">
              You May Also Like
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
              {relatedProducts.map((relatedProduct) => (
                <ProductCard key={relatedProduct.id} product={relatedProduct} />
              ))}
            </div>
          </div>
        </section>
      )}
    </ShopLayout>
  );
};

export default ProductPage;
